import { MouseEventHandler } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { FeatureFlagged } from '@backstage/core-app-api';

import { ConfidenceLogo } from '../Icons';
import { ChartBackground } from './ChartBackground';
import { Footer } from './Footer';
import { LoginErrorMessage } from './LoginErrorMessage';

const MAX_PAGE_WIDTH = 576;

const useStyles = makeStyles(theme => {
  return createStyles({
    root: {
      position: 'relative',
      display: 'flex',
      width: '100%',
      height: '100%',
      zIndex: 1,
      justifyContent: 'space-between',
      alignItems: 'center',
      flexDirection: 'column',
    },
    logo: {
      width: 80,
      minHeight: 80,
      marginTop: theme.spacing(9),
      marginBottom: theme.spacing(5),
    },
    container: {
      maxWidth: MAX_PAGE_WIDTH,
      flexGrow: 1,
      margin: '0 auto',
      width: '100%',
    },
  });
});

// eslint-disable-next-line @spotify-confidence/page-requires-tracking -- Pre-auth page in core-react cannot depend on tracking-react
export const LoginPage = ({
  onLogin,
  loading,
  error,
}: {
  onLogin?: MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  error?: Error;
}) => {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <ChartBackground>
      <Box
        className={classes.root}
        data-testid="login-page-root"
        position="relative"
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          <ConfidenceLogo className={classes.logo} color="currentColor" />

          <Typography variant="h3" component="h1" align="center">
            <span>
              Have an idea.
              <br /> Test it. Ship it. <br /> With Confidence.
            </span>
          </Typography>
          <Box className={classes.container}>
            {error && (
              <Alert severity="error">
                <LoginErrorMessage error={error} />
              </Alert>
            )}
            <Box
              display="flex"
              flexDirection="row"
              gridGap={16}
              alignItems="center"
              justifyContent="center"
              marginTop={4}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                data-testid="login-button"
                onClick={onLogin}
                disabled={loading}
              >
                {!loading ? 'Log in' : 'Authenticating ...'}
              </Button>
              <FeatureFlagged with="self-serve">
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  fullWidth
                  data-testid="signup-button"
                  onClick={() => navigate('/signup')}
                  disabled={loading}
                >
                  Sign up
                </Button>
              </FeatureFlagged>
            </Box>
          </Box>
        </Box>
        <Footer />
      </Box>
    </ChartBackground>
  );
};
